import { Stack } from '@mui/material';
import { AttendanceRecordSource } from '@schooly/analytics';
import { Group } from '@schooly/api';
import { useCellStates } from '@schooly/components/data-grid';
import { ExpandableSearch } from '@schooly/style';
import { isUserMatchedTheSearch } from '@schooly/utils/user-helpers';
import { FC, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { ModalEmpty } from '../../../components/uikit-components/Modal/ModalEmpty';
import { GroupPreviewAttendanceEmptyStubNoButton } from '../../Groups/GroupPreviewModal/GroupPreviewModalAttendance/GroupPreviewAttendanceEmptyStub/GroupPreviewAttendanceEmptyStubNoButton';
import { AttendanceSubjectGroupHeaderCell } from './common/AttendanceCells';
import { ATTENDANCE_SUBJECT_STYLING } from './common/attendanceStyling';
import { GroupGridMember } from './common/buildGroupGrid';
import { AttendanceGrid } from './common/AttendanceGrid';
import { AttendanceCellState, AttendanceGridColumn } from './common/types';
import { AttendanceGridShell } from './AttendanceSidebar/AttendanceGridShell';
import { useAttendanceGridForSubjectGroup } from './data/useAttendanceGridForSubjectGroup';

const renderGroupDayHeader = (column: AttendanceGridColumn) => (
  <AttendanceSubjectGroupHeaderCell column={column} />
);

export const AttendanceSubjectPreviewTable: FC<{
  group: Group;
  members: GroupGridMember[];
}> = ({ group, members }) => {
  const { formatMessage } = useIntl();
  const cellContext = useCellStates<AttendanceCellState>();
  const [search, setSearch] = useState('');

  const { columns, rows, codes, showNewerColumns, showOlderColumns, isTableLoading } =
    useAttendanceGridForSubjectGroup(group.id, group.validity, members);

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return query ? rows.filter((row) => isUserMatchedTheSearch(row.student, query)) : rows;
  }, [rows, search]);

  const renderContent = useCallback(() => {
    if (!isTableLoading && !columns.length) {
      return <GroupPreviewAttendanceEmptyStubNoButton />;
    }

    if (search.trim() && !filteredRows.length) {
      return <ModalEmpty />;
    }

    return (
      <AttendanceGrid
        rows={filteredRows}
        gridColumns={columns}
        styling={ATTENDANCE_SUBJECT_STYLING}
        primaryHeaderLabel={formatMessage({ id: 'section-Students' })}
        renderDayHeader={renderGroupDayHeader}
        getCellState={cellContext.getCellState}
        onPageRight={showNewerColumns}
        onPageLeft={showOlderColumns}
      />
    );
  }, [
    isTableLoading,
    filteredRows,
    columns,
    cellContext.getCellState,
    search,
    showNewerColumns,
    showOlderColumns,
    formatMessage,
  ]);

  return (
    <AttendanceGridShell codes={codes} groupId={group.id} source={AttendanceRecordSource.Group}>
      <Stack direction="row" alignItems="center" gap={1} justifyContent="flex-end" sx={{ mb: 1 }}>
        <ExpandableSearch
          value={search}
          onChange_MemoizedCallbackOnly={setSearch}
          placeholder={formatMessage({ id: 'people-Search' })}
          withDebounce
          disabled={isTableLoading}
        />
      </Stack>

      {renderContent()}
    </AttendanceGridShell>
  );
};
